// 商品グループツリー（横持ち → プレオーダー平坦リスト）の組み立てと探索（FKV3-12941）
//
// 商品階層は product_group_trees の横持ち（product_group_id1〜5 が葉までのパス）で届く。
// 倉庫ツリーと同じ操作感で選ばせるため、階層付きのプレオーダー平坦リストへ直す。
//
// 選択値は倉庫ツリーと同じく「末端（葉）ノード」で保持し、送信時に
// 「配下の葉がすべて選ばれているノード」へ畳んでから階層ごとにまとめる。
// 商品グループは階層Nの値で絞れば配下がすべて含まれるため、親を1件送れば足りる。

import type { ProductGroupTreeExtended } from '@/lib/types';

/** ツリー1ノード（配列順＝表示順のプレオーダー） */
export interface ProductGroupTreeNode {
  /**
   * ノードの一意キー。祖先のパスを含める。
   *
   * 同じ商品グループIDが複数の親の下にぶら下がることがあり、IDだけでは同一ノードに
   * 見えてしまう（片方の親を選んだだけでもう片方にもチェックが入る）。
   */
  key: string;
  /** product_group_id */
  id: string;
  /**
   * SQLで使う階層番号。`product_group_trees.product_group_id{depth}` の列番号に対応する。
   *
   * 表示上の深さ（level）とは一致しないことがある。中間の階層が未設定の行では
   * 表示は詰めるが、絞り込みに使う列番号は元のままでなければならない。
   */
  depth: number;
  /** 表示上の深さ（1始まり）。インデントと親子判定に使う */
  level: number;
  name: string;
  /** 子を持つか（持たないノードが選択値の単位になる） */
  hasChildren: boolean;
}

/** 送信する選択（階層ごとにまとめた商品グループID） */
export interface ProductGroupDepthSelection {
  depth: number;
  product_group_ids: string[];
}

/** 階層の最大数（product_group_trees が持つ列数） */
const MAX_DEPTH = 5;

/** 祖先のキーに自身を連ねた一意キーを作る */
function childKey(parentKey: string, depth: number, id: string): string {
  const self = `${depth}:${id}`;
  return parentKey ? `${parentKey}>${self}` : self;
}

/** ツリー1行から (列番号, id, name) を並べる。未設定の階層は飛ばして詰める */
function pathOf(tree: ProductGroupTreeExtended): { depth: number; id: string; name: string }[] {
  const ids = [
    tree.product_group_id1,
    tree.product_group_id2,
    tree.product_group_id3,
    tree.product_group_id4,
    tree.product_group_id5,
  ];
  const names = [
    tree.product_group_name1,
    tree.product_group_name2,
    tree.product_group_name3,
    tree.product_group_name4,
    tree.product_group_name5,
  ];

  const path: { depth: number; id: string; name: string }[] = [];
  for (let i = 0; i < MAX_DEPTH; i++) {
    const id = ids[i];
    // 中間の階層が未設定でも、その先の階層を捨てない（捨てるとツリーから消えて選べなくなる）。
    // 表示は詰めるが、絞り込みに使う列番号 depth は元の位置のまま持ち回る
    if (!id) continue;
    path.push({ depth: i + 1, id, name: names[i] || '(名称未設定)' });
  }
  return path;
}

interface Builder {
  key: string;
  id: string;
  depth: number;
  level: number;
  name: string;
  children: Map<string, Builder>;
}

/**
 * 指定タイプの商品階層をプレオーダー平坦リストにする。
 *
 * 同じ値が複数行に現れる（下位階層の数だけ行がある）ため、親の下でIDごとに畳む。
 * 並びは同一階層の名前順（五十音）で、倉庫ツリーと同じく親の直後に配下が続く。
 */
export function buildProductGroupTree(
  trees: ProductGroupTreeExtended[],
  typeId: string,
): ProductGroupTreeNode[] {
  if (!typeId) return [];

  const roots = new Map<string, Builder>();
  trees
    .filter((tree) => tree.product_group_type_id === typeId)
    .forEach((tree) => {
      let children = roots;
      let parentKey = '';
      pathOf(tree).forEach((value, index) => {
        const key = childKey(parentKey, value.depth, value.id);
        let node = children.get(key);
        if (!node) {
          node = {
            key,
            id: value.id,
            depth: value.depth,
            level: index + 1,
            name: value.name,
            children: new Map(),
          };
          children.set(key, node);
        }
        children = node.children;
        parentKey = key;
      });
    });

  const flat: ProductGroupTreeNode[] = [];
  const walk = (children: Map<string, Builder>) => {
    Array.from(children.values())
      .sort((a, b) => a.name.localeCompare(b.name, 'ja'))
      .forEach((node) => {
        flat.push({
          key: node.key,
          id: node.id,
          depth: node.depth,
          level: node.level,
          name: node.name,
          hasChildren: node.children.size > 0,
        });
        walk(node.children);
      });
  };
  walk(roots);
  return flat;
}

/** 親ノードのindex → 配下の葉ノードキー（倉庫ツリーの buildDescendantWarehouseIds と同じ形） */
export function buildDescendantLeafKeys(nodes: ProductGroupTreeNode[]): Map<number, string[]> {
  const map = new Map<number, string[]>();
  nodes.forEach((node, index) => {
    if (!node.hasChildren) return;
    const keys: string[] = [];
    for (let i = index + 1; i < nodes.length && nodes[i].level > node.level; i++) {
      if (!nodes[i].hasChildren) keys.push(nodes[i].key);
    }
    map.set(index, keys);
  });
  return map;
}

/** 葉ノードのキー一覧（一括選択・件数表示の母数） */
export function leafKeys(nodes: ProductGroupTreeNode[]): string[] {
  return nodes.filter((node) => !node.hasChildren).map((node) => node.key);
}

/**
 * 選択済みの葉から、送信する選択（階層ごとのグループID）を組み立てる。
 *
 * 配下の葉がすべて選ばれているノードは、その親1件に畳む（商品グループは階層Nの値で絞れば
 * 配下がすべて含まれるため、子を列挙する必要がない）。
 * 畳んだ結果は階層ごとにまとめ、SQLの `product_group_id{depth} in (...)` に対応させる。
 *
 * 同じIDが複数の親の下にある場合、送るのは (階層, ID) なのでどちらの親の下かは区別されない。
 * SQL がその階層の値でしか絞れない以上、区別のしようが無い（絞り込み結果は同じになる）。
 */
export function buildSelections(
  nodes: ProductGroupTreeNode[],
  selectedLeafKeys: Set<string>,
): ProductGroupDepthSelection[] {
  const descendants = buildDescendantLeafKeys(nodes);
  const byDepth = new Map<number, string[]>();
  const add = (node: ProductGroupTreeNode) => {
    const ids = byDepth.get(node.depth) ?? [];
    if (!ids.includes(node.id)) ids.push(node.id);
    byDepth.set(node.depth, ids);
  };

  // プレオーダーを上から見て、全選択のノードに当たったらその部分木は飛ばす
  let skipUntilLevel: number | null = null;
  nodes.forEach((node, index) => {
    if (skipUntilLevel !== null) {
      if (node.level > skipUntilLevel) return;
      skipUntilLevel = null;
    }
    if (!node.hasChildren) {
      if (selectedLeafKeys.has(node.key)) add(node);
      return;
    }
    const leaves = descendants.get(index) ?? [];
    if (leaves.length > 0 && leaves.every((key) => selectedLeafKeys.has(key))) {
      add(node);
      skipUntilLevel = node.level;
    }
  });

  return Array.from(byDepth, ([depth, product_group_ids]) => ({ depth, product_group_ids })).sort(
    (a, b) => a.depth - b.depth,
  );
}

/**
 * 送信済みの選択（階層ごとのグループID）を、画面の選択状態（葉のキー集合）へ戻す。
 *
 * 前回実行条件の復元に使う。親が指定されていれば配下の葉をすべて選択済みにする。
 * 同じIDが複数の親の下にある場合は、SQL の絞り込みと同じく**どちらもチェックが入る**
 * （どちらの親の下で選んだかは送信値に残らないため）。
 */
export function expandSelectionsToLeafKeys(
  nodes: ProductGroupTreeNode[],
  selections: ProductGroupDepthSelection[],
): Set<string> {
  const descendants = buildDescendantLeafKeys(nodes);
  const specified = new Set(
    selections.flatMap((selection) =>
      selection.product_group_ids.map((id) => `${selection.depth}:${id}`),
    ),
  );

  const selected = new Set<string>();
  nodes.forEach((node, index) => {
    if (!specified.has(`${node.depth}:${node.id}`)) return;
    if (node.hasChildren) {
      (descendants.get(index) ?? []).forEach((key) => selected.add(key));
    } else {
      selected.add(node.key);
    }
  });
  return selected;
}
